// orval mutator。生成された react-query hooks がここを通って fetch する。
// 標準 fetch を使うのは MSW v2 が service worker / node interceptor から
// fetch を確実に hook できるため (axios だと別経路になり MSW を貫通する場合がある)。
//
// orval は `customFetch<T>(url, init)` のシグネチャで呼び出す。
// 戻り値は { status, data, headers } の discriminated union (generated types)。
//
// 2xx 以外はここで ApiError として throw し、react-query 側の isError として
// 拾われるようにする (生成 hooks の error 型は ApiError)。

const BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? "";

export type ErrorType<Error> = Error;
export type BodyType<BodyData> = BodyData;

// 2xx 以外でサーバから返ってきたレスポンスを表す例外。
// 呼び出し側は instanceof ApiError で判定し、status / data を参照できる。
export class ApiError extends Error {
  override readonly name = "ApiError";
  constructor(
    readonly status: number,
    readonly data: unknown,
    readonly headers: Headers,
  ) {
    super(`API request failed with status ${status}`);
  }
}

export async function customFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  // FormData のときは Content-Type を設定しない。設定すると fetch が multipart の boundary を
  // 自動付与できなくなり、サーバがパートを解釈できなくなる (結果 PDF 取込で発覚)。
  if (!headers.has("Content-Type") && init?.body && !(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(`${BASE_URL}${url}`, {
    credentials: "same-origin",
    ...init,
    headers,
  });

  const text = await response.text();
  const data = text.length > 0 ? (JSON.parse(text) as unknown) : undefined;

  if (!response.ok) {
    throw new ApiError(response.status, data, response.headers);
  }

  // orval generated types は { status, data, headers } を期待する
  return {
    status: response.status,
    data,
    headers: response.headers,
  } as T;
}
